import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/queryClient";
import {
  getRequestedServiceProviders,
  getServiceProviderPaymentInstructionCodes,
  getServiceProviderRegistrationInfo,
  getServiceProviders,
} from "./services";
import {
  type IRequestedServiceProvider,
  type IServiceProvider,
  type IServiceProviderPaymentInstructionCodeRequest,
  type IServiceProviderRegistrationInfo,
  type IServiceProviderRegistrationInfoRequest,
} from "./types";

export const useServiceProvidersQuery = (
  jointBillServiceId: number,
  enabled?: boolean,
) =>
  useQuery<IServiceProvider[]>({
    queryKey: ["serviceProviders", jointBillServiceId],
    queryFn: () => getServiceProviders(jointBillServiceId),
    staleTime: Infinity,
    enabled,
  });

export const useServiceProviderRegistrationInfoQuery = (
  data: IServiceProviderRegistrationInfoRequest,
) =>
  useQuery<IServiceProviderRegistrationInfo>({
    queryKey: ["serviceProviders", "registrationInfo", data],
    queryFn: () => getServiceProviderRegistrationInfo(data),
    staleTime: Infinity,
  });

export const prefetchServiceProviderRegistrationInfo = async (
  data: IServiceProviderRegistrationInfoRequest,
) => {
  await queryClient.prefetchQuery({
    queryKey: ["serviceProviders", "registrationInfo", data],
    queryFn: () => getServiceProviderRegistrationInfo(data),
    staleTime: Infinity,
  });
};

export const useRequestedServiceProvidersQuery = () =>
  useQuery<IRequestedServiceProvider[]>({
    queryKey: ["requestedServiceProviders"],
    queryFn: () => getRequestedServiceProviders(),
    staleTime: Infinity,
  });

export const useServiceProviderPaymentInstructionCodesQuery = (
  data: IServiceProviderPaymentInstructionCodeRequest,
  enabled?: boolean,
) =>
  useQuery({
    queryKey: [
      "serviceProviders",
      "paymentInstructionCodes",
      data.serviceProviderId,
      data.paymentCode,
    ],
    queryFn: () => getServiceProviderPaymentInstructionCodes(data),
    staleTime: Infinity,
    enabled,
  });
